import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronRight, FileText, Shield, AlertCircle, Scale, Mail, Phone, Globe, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EFFECTIVE_DATE = "01 de enero de 2025";
const LAST_UPDATE = "10 de abril de 2026";

function LegalHeader({ company }) {
  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/superpoderes" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft size={18} className="text-gray-600" />
          </a>
          <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center">
            <FileText size={15} className="text-white" />
          </div>
          <span className="font-semibold text-gray-900">{company?.app_name || "OkBot"}</span>
        </div>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <a href="/terminos" className="text-red-600 font-medium border-b-2 border-red-600 pb-0.5">Términos</a>
          <a href="/privacidad" className="text-gray-500 hover:text-gray-900 transition-colors">Privacidad</a>
          <a href="/aviso-legal" className="text-gray-500 hover:text-gray-900 transition-colors">Aviso Legal</a>
          <a href="/cookies" className="text-gray-500 hover:text-gray-900 transition-colors">Cookies</a>
        </nav>
      </div>
    </header>
  );
}

function Section({ number, title, children }) {
  return (
    <section className="mb-10">
      <div className="flex items-start gap-4 mb-4">
        <span className="w-8 h-8 rounded-full bg-red-600 text-white text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{number}</span>
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="ml-12 space-y-3 text-gray-600 leading-relaxed">{children}</div>
    </section>
  );
}

function SubSection({ title, children }) {
  return (
    <div className="mt-4">
      <h3 className="text-base font-semibold text-gray-800 mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TableRow({ term, definition }) {
  return (
    <tr className="border-b border-gray-100">
      <td className="py-3 pr-6 font-medium text-gray-900 align-top w-40 whitespace-nowrap">{term}</td>
      <td className="py-3 text-gray-600">{definition}</td>
    </tr>
  );
}

export default function TermsPage() {
  const [company, setCompany] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/legal/public-info`).then(r => setCompany(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const c = company || {};
  const companyName = c.company_name || "OkBot";
  const email = c.email || "hola@okgo.mx";
  const privacyEmail = c.privacy_email || "privacidad@okgo.mx";
  const website = c.website || "https://okbot.okgo.mx";
  const appName = c.app_name || "OkBot";

  return (
    <div className="min-h-screen bg-gray-50">
      <LegalHeader company={company} />

      {/* Hero */}
      <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-4">
            <a href="/superpoderes" className="hover:text-white transition-colors">Inicio</a>
            <ChevronRight size={14} />
            <span>Términos y Condiciones</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <Scale size={36} className="text-red-200" />
            <div>
              <h1 className="text-4xl font-bold">Términos y Condiciones</h1>
              <p className="text-red-100 mt-1">Acuerdo de uso del servicio {appName}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 mt-8 pt-8 border-t border-red-500/40 text-sm text-red-100">
            <div><span className="text-red-300 block text-xs uppercase tracking-wide mb-1">Vigencia</span>{EFFECTIVE_DATE}</div>
            <div><span className="text-red-300 block text-xs uppercase tracking-wide mb-1">Última actualización</span>{LAST_UPDATE}</div>
            <div><span className="text-red-300 block text-xs uppercase tracking-wide mb-1">Jurisdicción</span>México</div>
            <div><span className="text-red-300 block text-xs uppercase tracking-wide mb-1">Legislación</span>LFCU · LFPDPPP · CCF</div>
          </div>
        </div>
      </div>

      {/* Aviso importante */}
      <div className="max-w-5xl mx-auto px-6 -mt-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex gap-4 items-start shadow-sm">
          <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-amber-800 text-sm leading-relaxed">
            <strong>Aviso importante:</strong> Al acceder, registrarte o utilizar la plataforma {appName}, aceptas íntegramente los presentes Términos y Condiciones. Si no estás de acuerdo con alguna disposición, debes abstenerte de usar el Servicio.
          </p>
        </div>
      </div>

      {/* Contenido */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10">

          {/* Identificación */}
          <div className="mb-10 p-6 bg-gray-50 rounded-xl border border-gray-200">
            <h2 className="text-base font-semibold text-gray-900 mb-4 uppercase tracking-wide text-xs text-gray-500">Identificación del Proveedor</h2>
            <table className="w-full">
              <tbody>
                <TableRow term="Razón Social" definition={companyName} />
                {c.rfc && <TableRow term="RFC" definition={c.rfc} />}
                {c.legal_rep && <TableRow term="Representante Legal" definition={c.legal_rep} />}
                {(c.address || c.city) && <TableRow term="Domicilio" definition={[c.address, c.city, c.state, c.zip_code, "México"].filter(Boolean).join(", ")} />}
                <TableRow term="Correo Electrónico" definition={email} />
                <TableRow term="Sitio Web" definition={website} />
              </tbody>
            </table>
          </div>

          <Section number="1" title="Definiciones">
            <p>Para efectos de los presentes Términos y Condiciones, los siguientes términos tendrán el significado que se indica a continuación:</p>
            <table className="w-full mt-4 border border-gray-200 rounded-lg overflow-hidden text-sm">
              <thead className="bg-gray-50"><tr><th className="py-3 px-4 text-left text-gray-700 font-semibold">Término</th><th className="py-3 px-4 text-left text-gray-700 font-semibold">Definición</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Plataforma / Servicio", `La aplicación web y/o móvil denominada "${appName}", incluyendo todos sus módulos, funcionalidades y contenidos.`],
                  ["Usuario", "Persona física mayor de 18 años que se registra y utiliza el Servicio para fines personales."],
                  ["Cuenta", "Perfil creado por el Usuario mediante su OkUser y PIN para acceder al Servicio."],
                  ["OkUser", "Nombre de usuario único elegido por el Usuario o asignado por el Administrador."],
                  ["PIN", "Número de Identificación Personal de 4 dígitos utilizado para autenticar al Usuario."],
                  ["Agente IA", "Sistema de inteligencia artificial integrado en la Plataforma que asiste al Usuario en sus tareas."],
                  ["Contenido", "Toda información, datos, textos, imágenes o materiales generados, subidos o transmitidos a través del Servicio."],
                  ["Proveedor", `${companyName}, titular y operadora del Servicio.`],
                ].map(([t, d]) => (
                  <tr key={t} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-medium text-gray-900 align-top">{t}</td>
                    <td className="py-3 px-4 text-gray-600">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section number="2" title="Objeto y Descripción del Servicio">
            <p>{appName} es una plataforma de asistente personal potenciada por inteligencia artificial que permite a los Usuarios:</p>
            <ul className="list-none space-y-2 mt-3">
              {[
                "Gestionar tareas, recordatorios, calendarios y notas personales",
                "Acceder a módulos de finanzas personales, salud y bienestar",
                "Utilizar herramientas de entretenimiento, música y viajes",
                "Interactuar con agentes de inteligencia artificial especializados",
                "Conectar con APIs de terceros a través del módulo RapidAPI Hub",
                "Compartir contenido y colaborar con otros usuarios de la plataforma",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4">El Servicio está disponible en modalidades de acceso gratuito y de pago, sujetas a los planes vigentes publicados en la Plataforma. El Proveedor se reserva el derecho de modificar, suspender o descontinuar cualquier funcionalidad en cualquier momento.</p>
          </Section>

          <Section number="3" title="Registro y Cuenta de Usuario">
            <SubSection title="3.1 Requisitos de registro">
              <p>Para utilizar el Servicio, el Usuario deberá:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li>Ser mayor de 18 años o contar con autorización de su tutor legal</li>
                <li>Proporcionar información verídica: nombre(s), apellido(s) y OkUser único</li>
                <li>Configurar un PIN de 4 dígitos para autenticación</li>
                <li>Aceptar los presentes Términos y la Política de Privacidad</li>
              </ul>
            </SubSection>
            <SubSection title="3.2 Responsabilidad del Usuario">
              <p>El Usuario es responsable de mantener la confidencialidad de su PIN y de todas las actividades realizadas bajo su Cuenta. Deberá notificar inmediatamente al Proveedor ante cualquier uso no autorizado a través de {email}.</p>
            </SubSection>
            <SubSection title="3.3 Campos de registro">
              <table className="w-full mt-2 border border-gray-200 rounded-lg overflow-hidden text-sm">
                <thead className="bg-gray-50"><tr><th className="py-2 px-4 text-left text-gray-700 font-medium">Campo</th><th className="py-2 px-4 text-left text-gray-700 font-medium">Obligatorio</th><th className="py-2 px-4 text-left text-gray-700 font-medium">Uso</th></tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    ["Nombre(s)", "Sí", "Identificación del Usuario en la Plataforma"],
                    ["Apellido(s)", "Sí", "Identificación del Usuario en la Plataforma"],
                    ["OkUser (nickname)", "Sí", "Identificador único de acceso"],
                    ["Correo electrónico", "No", "Recuperación de cuenta y comunicaciones"],
                    ["Número de WhatsApp", "No", "Notificaciones y comunicación directa"],
                  ].map(([f, o, u]) => (
                    <tr key={f} className="border-b border-gray-100">
                      <td className="py-2.5 px-4 font-medium text-gray-900">{f}</td>
                      <td className="py-2.5 px-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${o === "Sí" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>{o}</span>
                      </td>
                      <td className="py-2.5 px-4 text-gray-600 text-sm">{u}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SubSection>
          </Section>

          <Section number="4" title="Obligaciones y Conducta del Usuario">
            <SubSection title="4.1 Usos permitidos">
              <p>El Usuario se compromete a utilizar el Servicio única y exclusivamente para fines lícitos, personales y no comerciales, conforme a la legislación mexicana aplicable.</p>
            </SubSection>
            <SubSection title="4.2 Conductas prohibidas">
              <p>Queda estrictamente prohibido:</p>
              <ul className="list-disc pl-5 space-y-1 text-sm mt-2">
                <li>Usar el Servicio para actividades ilícitas o contrarias a la moral y las buenas costumbres</li>
                <li>Intentar acceder sin autorización a sistemas, cuentas o datos de terceros</li>
                <li>Distribuir virus, malware o cualquier código dañino</li>
                <li>Realizar ingeniería inversa, descompilar o desensamblar la Plataforma</li>
                <li>Usar sistemas automatizados (bots, scrapers) sin autorización previa del Proveedor</li>
                <li>Suplantar la identidad de otro Usuario o del Proveedor</li>
                <li>Transmitir contenido que viole derechos de propiedad intelectual de terceros</li>
                <li>Utilizar el Servicio para enviar comunicaciones comerciales no solicitadas (spam)</li>
              </ul>
            </SubSection>
          </Section>

          <Section number="5" title="Propiedad Intelectual">
            <p>Todo el contenido de la Plataforma, incluyendo sin limitación: software, código fuente, diseños, logotipos, textos, imágenes, gráficos, interfaces y marcas comerciales, son propiedad exclusiva de {companyName} o de sus licenciantes, y están protegidos por la Ley Federal del Derecho de Autor, la Ley de la Propiedad Industrial y los tratados internacionales aplicables.</p>
            <p className="mt-3">El Usuario conserva la titularidad del Contenido que suba o genere a través del Servicio, otorgando al Proveedor una licencia no exclusiva, gratuita y mundial para utilizarlo exclusivamente con el fin de prestar el Servicio.</p>
          </Section>

          <Section number="6" title="Privacidad y Protección de Datos Personales">
            <p>El tratamiento de los datos personales del Usuario se rige por nuestra <a href="/privacidad" className="text-red-600 hover:underline font-medium">Política de Privacidad</a>, la cual forma parte integrante de los presentes Términos y Condiciones.</p>
            <p className="mt-3">El Proveedor cumple con la <strong>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</strong> y su Reglamento, garantizando los derechos ARCO del Usuario (Acceso, Rectificación, Cancelación y Oposición).</p>
          </Section>

          <Section number="7" title="Servicios de Terceros e Integraciones">
            <p>La Plataforma integra servicios y APIs de terceros (incluyendo, sin limitación: servicios de inteligencia artificial, pasarelas de pago, APIs de datos a través de RapidAPI Hub). El uso de dichos servicios está sujeto a los términos y condiciones de cada proveedor externo.</p>
            <p className="mt-3">El Proveedor no se responsabiliza por la disponibilidad, exactitud, calidad o legalidad del contenido proveniente de servicios de terceros.</p>
          </Section>

          <Section number="8" title="Planes y Pagos">
            <table className="w-full border border-gray-200 rounded-lg overflow-hidden text-sm mt-2">
              <thead className="bg-gray-50"><tr><th className="py-3 px-4 text-left text-gray-700 font-semibold">Plan</th><th className="py-3 px-4 text-left text-gray-700 font-semibold">Precio (MXN)</th><th className="py-3 px-4 text-left text-gray-700 font-semibold">Características</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Free", "$0/mes", "Acceso básico a módulos principales"],
                  ["Esencial", "$149/mes", "Módulos completos + integraciones básicas"],
                  ["Pro", "$349/mes", "Todas las funcionalidades + APIs avanzadas"],
                  ["Elite", "$799/mes", "Acceso ilimitado + soporte prioritario"],
                ].map(([plan, precio, desc]) => (
                  <tr key={plan} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-semibold text-gray-900">{plan}</td>
                    <td className="py-3 px-4 text-red-600 font-medium">{precio}</td>
                    <td className="py-3 px-4 text-gray-600">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-4 text-sm">Los precios están expresados en Pesos Mexicanos (MXN) e incluyen el Impuesto al Valor Agregado (IVA) cuando aplique, conforme a la legislación fiscal vigente. El cobro se realiza de manera recurrente según el ciclo del plan contratado.</p>
            <p className="mt-3 text-sm">El Usuario podrá cancelar su suscripción en cualquier momento. No se realizarán reembolsos por períodos parciales ya facturados, salvo en los casos previstos por la Ley Federal de Protección al Consumidor.</p>
          </Section>

          <Section number="9" title="Limitación de Responsabilidad">
            <p>En la máxima extensión permitida por la legislación mexicana aplicable, el Proveedor no será responsable por:</p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-3">
              <li>Daños indirectos, incidentales, especiales o consecuentes derivados del uso o imposibilidad de uso del Servicio</li>
              <li>Pérdida de datos, ingresos o beneficios económicos</li>
              <li>Interrupciones del Servicio causadas por fuerza mayor, caso fortuito o fallas de terceros</li>
              <li>La exactitud de las respuestas generadas por los sistemas de inteligencia artificial</li>
              <li>Conductas de terceros usuarios de la Plataforma</li>
            </ul>
            <p className="mt-4">La responsabilidad total del Proveedor frente al Usuario no excederá el monto pagado por el Usuario durante los tres (3) meses anteriores al evento que originó el daño.</p>
          </Section>

          <Section number="10" title="Modificaciones al Servicio y a los Términos">
            <p>El Proveedor se reserva el derecho de modificar, suspender o discontinuar el Servicio, ya sea de manera temporal o permanente, con o sin previo aviso.</p>
            <p className="mt-3">Las modificaciones a los presentes Términos serán notificadas al Usuario con al menos <strong>15 días naturales de anticipación</strong> a través del correo electrónico registrado o mediante aviso visible en la Plataforma. El uso continuado del Servicio después de la entrada en vigor de los cambios constituirá aceptación de los mismos.</p>
          </Section>

          <Section number="11" title="Terminación y Suspensión de Cuenta">
            <SubSection title="11.1 Suspensión por incumplimiento">
              <p>El Proveedor podrá suspender o cancelar la Cuenta del Usuario, sin responsabilidad alguna, cuando detecte el incumplimiento de los presentes Términos, conductas fraudulentas o actividades ilícitas.</p>
            </SubSection>
            <SubSection title="11.2 Cancelación por el Usuario">
              <p>El Usuario podrá solicitar la cancelación de su Cuenta en cualquier momento enviando un correo a {email}. Los datos personales serán tratados conforme a la Política de Privacidad.</p>
            </SubSection>
          </Section>

          <Section number="12" title="Legislación Aplicable y Jurisdicción">
            <p>Los presentes Términos y Condiciones se rigen por las leyes vigentes en los <strong>Estados Unidos Mexicanos</strong>, en particular:</p>
            <ul className="list-disc pl-5 space-y-1 text-sm mt-3">
              <li>Ley Federal de Protección al Consumidor (LFPC)</li>
              <li>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</li>
              <li>Código Civil Federal (CCF)</li>
              <li>Código de Comercio</li>
              <li>Ley Federal del Derecho de Autor</li>
            </ul>
            <p className="mt-4">Para la resolución de cualquier controversia derivada de los presentes Términos, las partes se someten expresamente a la jurisdicción de los <strong>Tribunales competentes de la Ciudad de México</strong>, renunciando a cualquier otro fuero que pudiera corresponderles por razón de su domicilio presente o futuro.</p>
          </Section>

          <Section number="13" title="Contacto">
            <p>Para cualquier consulta relacionada con estos Términos y Condiciones, puede contactarnos a través de:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              {[
                { icon: <Mail size={18} className="text-red-600" />, label: "Correo", value: email, href: `mailto:${email}` },
                { icon: <Globe size={18} className="text-red-600" />, label: "Sitio Web", value: website, href: website },
                ...(c.phone ? [{ icon: <Phone size={18} className="text-red-600" />, label: "Teléfono", value: c.phone, href: `tel:${c.phone}` }] : []),
              ].map(({ icon, label, value, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-xl hover:border-red-200 hover:bg-red-50 transition-colors group">
                  {icon}
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
                    <p className="text-gray-900 font-medium text-sm group-hover:text-red-700">{value}</p>
                  </div>
                </a>
              ))}
            </div>
          </Section>

        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm">© {new Date().getFullYear()} {companyName}. Todos los derechos reservados.</p>
          <div className="flex gap-6 text-sm">
            <a href="/terminos" className="text-red-400 hover:text-red-300 transition-colors">Términos</a>
            <a href="/privacidad" className="hover:text-gray-200 transition-colors">Privacidad</a>
            <a href="/aviso-legal" className="hover:text-gray-200 transition-colors">Aviso Legal</a>
            <a href="/cookies" className="hover:text-gray-200 transition-colors">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
